import type { AskAiFilterChoice } from '@digital-twins/shared-types'

import type { FilterCondition, PopulationFilter } from '@/types/routes/test-results'

function isSimpleCondition(entry: FilterCondition | PopulationFilter): entry is FilterCondition {
  if ('conditions' in entry) {
    return false
  }
  return !entry.groupType && (entry.operator === 'equals' || entry.operator === 'in')
}

function conditionValues(entry: FilterCondition): Array<string> {
  return (Array.isArray(entry.value) ? entry.value : [entry.value])
    .map((value) => String(value))
    .filter((value) => value.length > 0)
}

/**
 * The picker choices an `initialFilter` reopens with: each top-level `equals`
 * or `in` condition on a single column. Anything the picker cannot show as its
 * own selection (an OR at the top, nested groups, grouped questions, other
 * operators) is left out rather than guessed.
 */
export function choicesFromInitialFilter(
  filter: PopulationFilter | null | undefined,
): Array<AskAiFilterChoice> {
  const conditions = filter?.conditions ?? []
  if (filter?.logic === 'OR' && conditions.length > 1) {
    return []
  }
  const choices: Array<AskAiFilterChoice> = []
  for (const entry of conditions) {
    if (!isSimpleCondition(entry)) {
      continue
    }
    const values = conditionValues(entry)
    if (values.length > 0) {
      choices.push({ column: entry.field, values })
    }
  }
  return choices
}

/**
 * True when the picker can reopen this filter whole: no filter, or a top-level
 * AND (or a single condition) of single-column `equals`/`in` conditions with
 * values. False means editing it in the picker would drop conditions.
 */
export function isFilterFullyEditable(
  filter: PopulationFilter | null | undefined,
): boolean {
  const conditions = filter?.conditions ?? []
  if (conditions.length === 0) {
    return true
  }
  if (filter?.logic === 'OR' && conditions.length > 1) {
    return false
  }
  return conditions.every(
    (entry) => isSimpleCondition(entry) && conditionValues(entry).length > 0,
  )
}
